import { Box, Button, Flex, IconButton, Image, Text, Separator } from '@chakra-ui/react';
import { useSubscriptionCache, useUserCache } from 'src/apollo/cache';
import AlituTickIcon from 'src/assets/icons/AlituTickIcon';
import DefaultArtworkIcon from 'src/assets/icons/DefaultArtworkIcon';
import TrashIcon from 'src/assets/icons/TrashIcon';
import { ShowFragmentFragment } from 'src/generated/graphql';
import useSubscriptionManagementLock from 'src/pages/my-account/utils/useSubscriptionManagementLock';
import colors from 'src/theme/colors';
import assert from 'src/utils/assert';
import getArtworkVariant from 'src/utils/getArtworkVariant';
import hexToRgb from 'src/utils/hexToRgb';
import { useMyShowsContext } from './MyShowsContext';
import RenewShowButton from './RenewShowButton';

export default function Show({
  show,
  onSwitchToShow,
}: {
  show: ShowFragmentFragment;
  onSwitchToShow: () => void;
}) {
  const user = useUserCache();
  assert(user, 'user not found in cache');
  const { setContentType, setShowToRemove, isMyAccount, onOpen } = useMyShowsContext();
  const subscription = useSubscriptionCache();
  const { locked } = useSubscriptionManagementLock(subscription);

  function getTitleWidth() {
    if (show.showExpiresAt) {
      if (isMyAccount) {
        return '140px';
      }
      return '200px';
    }
    return '300px';
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          {show.artworkUrl ? (
            <Image
              src={
                // if artwork is from 3rd party feed, there are no variants
                show.host === 'alitu'
                  ? `${getArtworkVariant(show.artworkUrl, '120px')}`
                  : show.artworkUrl
              }
              alt="show cover art"
              width="36px"
              height="36px"
              borderRadius="8px"
            />
          ) : (
            <Flex
              width="36px"
              height="36px"
              borderRadius="8px"
              justifyContent="center"
              alignItems="center"
              bg={colors.podcastBlue}
            >
              <DefaultArtworkIcon />
            </Flex>
          )}
          <Text
            as="h4"
            ml="15px"
            color={colors.podcastShade600}
            textOverflow="ellipsis"
            overflow="hidden"
            w={getTitleWidth()}
            whiteSpace="nowrap"
          >
            {show.name}
          </Text>
        </Flex>
        <Flex alignItems="center">
          {show.showExpiresAt && !locked && (
            <RenewShowButton showId={show.id} showExpiresAt={show.showExpiresAt} />
          )}
          {user.activeProjectId === show.id ? (
            <AlituTickIcon width="24px" height="24px" />
          ) : (
            <Flex>
              <Box>
                {!show.showExpiresAt && !locked && (
                  <IconButton
                    h="100%"
                    visual="flat"
                    bg={hexToRgb(colors.podcastRed600, 0.1)}
                    _hover={{ bg: hexToRgb(colors.podcastRed600, 0.05) }}
                    _active={{ bg: hexToRgb(colors.podcastRed600, 0.15) }}
                    onClick={() => {
                      setContentType('remove-show');
                      setShowToRemove(show.id);
                      if (isMyAccount) {
                        onOpen();
                      }
                    }}
                    aria-label="Remove show"
                    marginRight="10px"
                  >
                    <TrashIcon color={colors.podcastRed600} />
                  </IconButton>
                )}
              </Box>
              <Button onClick={onSwitchToShow} visual="quaternary">
                Switch to
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Separator visual="signUp" mt="12px" mb="12px" />
    </>
  );
}
